import { forwardRef, useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { CheckIcon, ChevronDownIcon } from '@/components/icons';
import { useDismiss } from '@/hooks/useDismiss';
import { cx } from '@/utils/cx';
import styles from '@/styles/components/SelectField/style.scss';

export interface SelectOption {
  value: string;
  label: string;
  /** Secondary line under the label. */
  description?: string;
  /** Shown before the label (e.g. an Avatar), in the field and in the list. */
  leading?: ReactNode;
}

export interface SelectFieldProps {
  label?: string;
  /** Accessible name when there is no visible label. */
  'aria-label'?: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  /** Shown until a value is chosen. */
  placeholder?: string;
  autoFocus?: boolean;
  /** md (40px) for toolbars; lg (44px, the default) in forms. */
  size?: 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}

/**
 * Custom single select (WAI-ARIA "select-only combobox"). Focus stays on the trigger and the
 * highlighted option is announced through aria-activedescendant.
 * Keys: ↑/↓/Home/End move, Enter/Space/Tab pick, Esc closes, typing a letter jumps to it.
 */
export const SelectField = forwardRef<HTMLButtonElement, SelectFieldProps>(function SelectField(
  { label, 'aria-label': ariaLabel, options, value, onChange, placeholder = 'Select', autoFocus, size = 'lg', disabled, className },
  ref,
) {
  const id = useId();
  const labelId = `${id}-label`;
  const listId = `${id}-list`;
  const optionId = (i: number) => `${id}-opt-${i}`;

  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = options[selectedIndex];

  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, rootRef);

  const openAt = (index: number) => {
    if (options.length === 0) return;
    setActive(Math.max(0, Math.min(options.length - 1, index)));
    setOpen(true);
  };

  const pick = (index: number) => {
    const option = options[index];
    setOpen(false);
    if (option && option.value !== value) onChange(option.value);
  };

  // Keep the highlighted option visible when moving with the keyboard.
  useEffect(() => {
    if (open) listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const jumpToLetter = (key: string) => {
    const start = open ? active + 1 : selectedIndex + 1;
    for (let n = 0; n < options.length; n++) {
      const i = (start + n) % options.length;
      if (options[i]!.label.toLowerCase().startsWith(key.toLowerCase())) return openAt(i);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const last = options.length - 1;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openAt(selectedIndex >= 0 ? selectedIndex : 0);
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        openAt(e.key === 'Home' ? 0 : last);
      } else if (e.key.length === 1 && /\S/.test(e.key)) {
        jumpToLetter(e.key);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (e.altKey) return pick(active);
        return setActive((i) => Math.min(last, i + 1));
      case 'ArrowUp':
        e.preventDefault();
        if (e.altKey) return pick(active);
        return setActive((i) => Math.max(0, i - 1));
      case 'Home':
        e.preventDefault();
        return setActive(0);
      case 'End':
        e.preventDefault();
        return setActive(last);
      case 'Enter':
      case ' ':
        e.preventDefault();
        return pick(active);
      case 'Tab':
        // Pick the highlighted option and let focus move on.
        return pick(active);
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) jumpToLetter(e.key);
    }
  };

  return (
    <div ref={rootRef} className={cx(styles.wrapper, className)}>
      {label && (
        <span id={labelId} className={styles.label}>
          {label}
        </span>
      )}

      <button
        ref={ref}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-labelledby={label ? labelId : undefined}
        aria-label={label ? undefined : ariaLabel}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        autoFocus={autoFocus}
        disabled={disabled}
        className={cx(styles.trigger, size === 'md' && styles.md, open && styles.triggerOpen)}
        onClick={() => (open ? setOpen(false) : openAt(selectedIndex >= 0 ? selectedIndex : 0))}
        onKeyDown={onKeyDown}
      >
        {selected ? (
          <span className={styles.value}>
            {selected.leading}
            <span className={styles.valueLabel}>{selected.label}</span>
          </span>
        ) : (
          <span className={styles.placeholder}>{placeholder}</span>
        )}
        <ChevronDownIcon size={18} className={cx(styles.chevron, open && styles.chevronOpen)} />
      </button>

      {open && (
        <ul ref={listRef} id={listId} role="listbox" aria-labelledby={label ? labelId : undefined} aria-label={label ? undefined : ariaLabel} className={styles.menu}>
          {options.map((o, i) => {
            const isSelected = o.value === value;
            return (
              <li
                key={o.value}
                id={optionId(i)}
                role="option"
                aria-selected={isSelected}
                data-index={i}
                className={cx(styles.option, i === active && styles.optionActive, isSelected && styles.optionSelected)}
                onPointerMove={() => setActive(i)}
                // Keep focus on the trigger.
                onPointerDown={(e) => e.preventDefault()}
                onClick={() => pick(i)}
              >
                {o.leading}
                <span className={styles.optionText}>
                  <span className={styles.optionLabel}>{o.label}</span>
                  {o.description && <span className={styles.optionDescription}>{o.description}</span>}
                </span>
                {isSelected && <CheckIcon size={16} className={styles.check} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
});
